import React, { useCallback, useContext, useMemo } from 'react';
import * as HenUi from '@highereducation/hen-ui';
import type { SlotHostComponent } from '@highereducation/hen-ui';
import {
  INLINE_PHRASING_TAGS,
  isSafeInlineLinkHref,
  useModalHostOptional,
} from '@highereducation/hen-ui';
import { Render } from '@puckeditor/core';
import type { Config, Data } from '@puckeditor/core';
import type {
  DslConfigPayload,
  DslRenderErrorFallback,
  DslRenderErrorHandler,
} from '@de/types/types';
import { DslCareerExplorer } from '../../../career-explorer/composites/components/CareerExplorer';
import {
  DataSourceManagerProvider,
  useOptionalDataSourceManager,
} from '@de/providers/DataSourceManager';
import { DataRepeater } from '@de/components/DataRepeater';
import { applyFetchPreconditions } from '@de/utils/decisions/applyFetchPreconditions';
import { collectFactBindings } from '@de/utils/decisions/collectFactBindings';
import {
  Variant,
  VARIANT_SLOT_NAMES,
  VARIANT_TYPE,
  type HostSlotReservedSize,
} from '@de/components/Variant';
import { DECISIONS_CONTEXT_KEY } from '@de/utils/decisions/embeddedDecisions';
import { HostSlot } from '@de/components/HostSlot';
import { PlatformForm } from '@de/components/PlatformForm';
import { DslConfigOwnerProvider } from '@de/providers/DslConfigOwnerContext';
import { extractPaths } from '@de/utils/interpolation';
import { warnOnce } from '@de/utils/warnOnce';
import type { DataSourceDeclaration } from '@de/providers/DataSourceManager.types';
import { isSupportedDataSourceDeclaration } from '@de/utils/data-sources/data-source-kind';
import {
  applyBindings,
  isBindingLanguageKey,
} from '@de/utils/binding-enhancer';
import { COMPONENT_TRACKING_DEFAULTS } from '@de/components/Trackable';
import { overlayComponentTrackingDefaults } from '@de/utils/applyHostTrackingDefaults';
import {
  ActionDispatcherContext,
  NOOP_ACTION_DISPATCHER,
  type ActionDispatcher,
} from '@de/utils/action-resolver/ActionDispatcher';
import { dispatchNamedModalAction } from '@de/utils/configurable-step/dispatchNamedModalAction';
import { FORM_COMPONENT_TYPE } from '@de/utils/configurable-step/findBoundComponent';
import { useDynamicExperienceContext } from '@de/providers/DynamicExperienceProvider';
import {
  useItemScope,
  useItemScopeIndexPath,
} from '@de/providers/ItemScopeContext';
import { useTracking } from '@de/providers/TrackingProvider';
import {
  ResultsPageConfigProvider,
  useResultsPageConfig,
  useResultsPageConfigOptional,
} from '@de/providers/ResultsPageConfigProvider';
import {
  ComparisonProvider,
  useComparisonOptional,
} from '@de/providers/ComparisonProvider';
import {
  useComparisonForProgram,
  useSubmittedForProgram,
  SUBMITTED_CTA_LABEL,
} from '@de/components/DegreeCardDetailed';
import { useLeadFlowOptional } from '@de/providers/LeadFlowProvider';
import type { Result } from '@de/types/result';
import {
  extractResultsPageConfig,
  isResultLike,
  projectRequestContext,
} from '@de/utils/configurable-step';
import { synthesizeOptionChildren } from '@de/utils/option-children';
import ComparisonRuntime from '../../ComparisonModal/ComparisonRuntime';

const DslDegreeCardDetailed: React.FC<Record<string, unknown>> = (props) => {
  const DegreeCardDetailed =
    HenUi.DegreeCardDetailed as unknown as React.ComponentType<
      Record<string, unknown>
    >;
  const { shouldShowComparison } = useResultsPageConfig();
  const item = useItemScope();
  const scopedItem = isResultLike(item) ? item : undefined;
  const canCompare = shouldShowComparison && Boolean(scopedItem);

  // Source the compare beam's correlation + position from the same
  // tracking context the card's other events use: `viewCorrelationId`
  // off the inherited flow `eventContext`, `correlationId` off the flow
  // `formViewedCorrelationId`, and a 1-based `position` derived from the
  // repeater row's index path (`useItemScopeIndexPath`). Without these
  // the compare `ElementClicked` was beamed with undefined correlation
  // ids and no position.
  const tracking = useTracking();
  const indexPath = useItemScopeIndexPath();
  const rowSegment = indexPath?.split('.').pop();
  const rowIndex =
    rowSegment !== undefined &&
    rowSegment !== '' &&
    !Number.isNaN(Number(rowSegment))
      ? Number(rowSegment)
      : undefined;
  const comparisonProps = useComparisonForProgram(
    canCompare ? scopedItem : undefined,
    {
      position: rowIndex !== undefined ? rowIndex + 1 : undefined,
      viewCorrelationId: tracking?.eventContext?.viewCorrelationId,
      correlationId: tracking?.eventContext?.formViewedCorrelationId,
    },
  );
  const { isProgramSubmitted } = useSubmittedForProgram(scopedItem);
  const leadFlow = useLeadFlowOptional();

  // Submitted-state override. Applied AFTER `{...props}` so a
  // bound author `ctaLabel` shows pre-submission and the platform owns the
  // governed submitted label + reopen-flow once a lead was submitted.
  // Clicking the "Submitted" CTA routes back through the governed
  // `openLeadFlow`, which treats re-clicking an already-submitted program
  // as a revisit (not a duplicate) and lands the drawer directly on the
  // exit-strategy view ("What to expect from the advisor?").
  const submittedOverride: { ctaLabel?: string; onCtaClick?: () => void } =
    isProgramSubmitted && scopedItem
      ? {
          ctaLabel: SUBMITTED_CTA_LABEL,
          onCtaClick: () => leadFlow?.openLeadFlow(scopedItem),
        }
      : {};

  // `createBoundRender` wires the card's click behaviour onto a single
  // root `onClick`: the `kind: 'product'` `ProductClicked` dispatch (the
  // `componentTrackingDefaults` registry marks `DegreeCardDetailed` as a
  // product surface) AND any authored `_action.onClick` (e.g.
  // `results.openLeadFlow`, which opens the governed drawer / PII
  // confirmation).
  const boundOnClick = props.onClick as
    | ((event: React.MouseEvent<HTMLElement>) => void)
    | undefined;

  // The effective CTA handler — the governed submitted-state reopen when a
  // lead was already submitted, otherwise the binding-enhancer's composed
  // `onCtaClick` (which carries an authored `_action.onCtaClick:
  // results.openLeadFlow`). Reused by `handleCardClick` so a tap anywhere
  // on the card body opens the lead form *exactly* like the CTA (champion
  // parity, EDUN-13207) — same params, `selectedOfferingId` enrichment,
  // and submitted-state revisit handling.
  const effectiveCtaClick = (submittedOverride.onCtaClick ??
    (props.onCtaClick as
      | ((event?: React.MouseEvent<HTMLElement>) => void)
      | undefined)) as
    | ((event?: React.MouseEvent<HTMLElement>) => void)
    | undefined;

  const handleCardClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    const target = event.target as HTMLElement;
    // Read More toggle owns its own expand/collapse affordance.
    if (target.closest?.('button[aria-controls]')) return;
    // Inside the footer (Compare + CTA), the Compare control — the
    // `role="checkbox"` button and its associated label — fires its own
    // `ElementClicked` and must not open the drawer or fire
    // `ProductClicked`. A CTA click fires only `ProductClicked` here; the
    // CTA button's own `onCtaClick` already opens the lead form, so we do
    // NOT also invoke `effectiveCtaClick` (that would double-open).
    if (target.closest?.('[data-degree-card-footer]')) {
      const isCta = target.closest?.('a[href], button:not([role="checkbox"])');
      if (!isCta) return;
      boundOnClick?.(event);
      return;
    }
    // Card body: mirror the CTA — fire `ProductClicked` (boundOnClick) and
    // then open the lead form (effectiveCtaClick). When the open is instead
    // authored on the card node (`_action.onClick`), it already rides on
    // boundOnClick and `effectiveCtaClick` is absent, so the card still
    // opens exactly once.
    boundOnClick?.(event);
    effectiveCtaClick?.(event);
  };

  // Wrap the root click whenever the card can act — a tracked product
  // click and/or a lead-form open. Preserve the prior behaviour when the
  // card renders outside a `<TrackingProvider>` / without any click action:
  // no wrapped handler, pass through whatever `props.onClick` was.
  const isClickable = Boolean(boundOnClick) || Boolean(effectiveCtaClick);
  const onClick = isClickable ? handleCardClick : props.onClick;
  // Signal interactivity (champion parity). Merge with any author-supplied
  // className rather than clobbering it.
  const className = isClickable
    ? [props.className, 'cursor-pointer'].filter(Boolean).join(' ')
    : (props.className as string | undefined);

  return (
    <DegreeCardDetailed
      {...props}
      compare={canCompare && comparisonProps.compare}
      compareChecked={canCompare ? comparisonProps.compareChecked : false}
      compareDisabled={canCompare ? comparisonProps.compareDisabled : false}
      onCompareChange={canCompare ? comparisonProps.onCompareChange : undefined}
      {...submittedOverride}
      onClick={onClick}
      className={className}
    />
  );
};

/**
 * Editorial Listing card with the same compare integration as
 * {@link DslDegreeCardDetailed}, so an *entry*-point Editorial Listing
 * config can opt into the platform compare feature via
 * `resultsPage.comparison`. The comparison provider stack is mounted by
 * the standalone `DslConfig` render path (host bridge does not own it for
 * entry configs), so this wrapper only needs to derive per-row compare
 * props and feed them to the hen-ui `EditorialListing`.
 *
 * Differences from the result-page card: Editorial Listing has no
 * governed lead flow (it links out via its own "Visit Site" CTA), so
 * there is no submitted-state override. Compare identity is the numeric
 * `programId`; offerings items that lack a resolvable id coerce to `0`,
 * which would collide across rows, so only rows carrying a real id are
 * compare-eligible.
 */
const DslEditorialListing: React.FC<Record<string, unknown>> = (props) => {
  const EditorialListing =
    HenUi.EditorialListing as unknown as React.ComponentType<
      Record<string, unknown>
    >;
  const { shouldShowComparison } = useResultsPageConfig();
  const item = useItemScope();
  const scopedItem = isResultLike(item) ? item : undefined;
  const canCompare =
    shouldShowComparison &&
    Boolean(scopedItem) &&
    (scopedItem?.programId ?? 0) > 0;

  const tracking = useTracking();
  const indexPath = useItemScopeIndexPath();
  const rowSegment = indexPath?.split('.').pop();
  const rowIndex =
    rowSegment !== undefined &&
    rowSegment !== '' &&
    !Number.isNaN(Number(rowSegment))
      ? Number(rowSegment)
      : undefined;
  const comparisonProps = useComparisonForProgram(
    canCompare ? scopedItem : undefined,
    {
      position: rowIndex !== undefined ? rowIndex + 1 : undefined,
      viewCorrelationId: tracking?.eventContext?.viewCorrelationId,
      correlationId: tracking?.eventContext?.formViewedCorrelationId,
    },
  );

  // The Compare checkbox lives inside the card; if an author wired a root
  // `_action.onClick`, its click would bubble to that handler. Skip the
  // Compare control (the `role="checkbox"` button + its label) so toggling
  // Compare never triggers the card action — it emits its own
  // CompareAdded / CompareRemoved beam via `onCompareChange`.
  const boundOnClick = props.onClick as
    | ((event: React.MouseEvent<HTMLElement>) => void)
    | undefined;

  const handleCardClick = (event: React.MouseEvent<HTMLElement>): void => {
    const target = event.target as HTMLElement;
    if (target.closest?.('button[aria-controls]')) return;
    if (target.closest?.('[data-editorial-listing-footer]')) {
      const isCta = target.closest?.('a[href], button:not([role="checkbox"])');
      if (!isCta) return;
    }
    boundOnClick?.(event);
  };

  const onClick = boundOnClick ? handleCardClick : props.onClick;

  return (
    <EditorialListing
      {...props}
      compare={canCompare && comparisonProps.compare}
      compareChecked={canCompare ? comparisonProps.compareChecked : false}
      compareDisabled={canCompare ? comparisonProps.compareDisabled : false}
      onCompareChange={canCompare ? comparisonProps.onCompareChange : undefined}
      onClick={onClick}
    />
  );
};

/**
 * Renders a saved DSL payload using Puck's native `<Render>` component
 * from `@puckeditor/core` — the same engine the admin editor uses.
 *
 * Why this file does what it does
 * -------------------------------
 * 1. Puck renders a `Data` tree by looking up `config.components[type].render`
 *    for each `{ type, props }` node. We build that config from the hen-ui
 *    barrel at runtime — no catalog fetch, no context provider.
 * 2. Nested components inside slot-shaped props are only rendered when the
 *    config declares those props as `{ type: "slot" }`. Since we don't ship a
 *    schema, we infer slot props by scanning the incoming `Data`: any prop
 *    whose value is an array of `{ type, props }` (or the known default `{}`
 *    that the admin occasionally persists) is registered as a slot for its
 *    component type.
 * 3. Puck's internal walker assumes every slot value is an array. Payloads
 *    produced by older editor versions sometimes persist a malformed slot as
 *    `{}` or `null`, which trips `arr.some is not a function` deep inside
 *    `@puckeditor/core`. We normalize those to `[]` before handing the data
 *    to `<Render>`.
 * 4. A `DslConfigErrorBoundary` wraps the render so a still-malformed payload
 *    can't crash the surrounding flow step (per SAD §14.3).
 */
const barrelExports: Record<string, unknown> = { ...HenUi };

const isReactComponent = (
  value: unknown,
): value is React.ComponentType<unknown> =>
  typeof value === 'function' ||
  (typeof value === 'object' &&
    value !== null &&
    '$$typeof' in (value as object));

type ComponentNode = { type: string; props?: Record<string, unknown> };

function isAuthoredNode(value: unknown): value is ComponentNode {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as ComponentNode).type === 'string'
  );
}

function renderAuthoredNode(
  node: ComponentNode,
  config: Config,
  slotFields: SlotFieldMap,
): React.ReactNode {
  const entry = config.components[node.type];
  if (!entry?.render) return null;
  const props = { ...(node.props ?? {}) };
  // A variant's renderer picks one raw slot template and renders it itself,
  // so its slots stay authored nodes rather than React elements.
  const slotKeys =
    node.type === VARIANT_TYPE ? [] : Object.keys(slotFields[node.type] ?? {});
  for (const slotKey of slotKeys) {
    const value = props[slotKey];
    if (Array.isArray(value)) {
      props[slotKey] = value.map((child, index) => {
        if (isPuckTextNode(child)) return extractPuckText(child);
        if (!isAuthoredNode(child)) return child;
        const rendered = renderAuthoredNode(child, config, slotFields);
        return React.isValidElement(rendered)
          ? React.cloneElement(rendered, { key: index })
          : rendered;
      });
    } else if (isPuckTextNode(value)) {
      props[slotKey] = extractPuckText(value);
    } else if (isAuthoredNode(value)) {
      props[slotKey] = renderAuthoredNode(value, config, slotFields);
    }
  }
  // An element, not a call. The bound renderer reads the row from
  // ItemScopeProvider while React reconciles it. Calling it here would
  // run that read before the provider exists and hand DataRepeater the
  // wrapper it returns, so the wrapper would be treated as the shell.
  return React.createElement(
    entry.render as React.ComponentType<Record<string, unknown>>,
    props,
  );
}

const INLINE_COPY_HOSTS = new Set(['Text', 'Heading', 'Brow', 'FeedbackBox']);
const INLINE_CHILD_TYPES = new Set(['Text', 'Link', 'PuckText']);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Synthetic Puck types this renderer registers itself rather than looking
 * up on the hen-ui barrel. They are still layout nodes: if
 * `isComponentNode` rejects them, `toSlotChild` wipes them from any
 * parent slot (Flex, CarouselItem, …) and the nested subtree never mounts.
 */
const RUNTIME_PRIMITIVE_TYPES = new Set([
  'platform.dataRepeater',
  VARIANT_TYPE,
  'platform.hostSlot',
  FORM_COMPONENT_TYPE,
]);

/**
 * True for hen-ui / PuckText / runtime-primitive nodes with a real `props`
 * bag. Structured data that happens to have a string `type` (chart configs,
 * catalog field shapes, etc.) must not be treated as copy nodes.
 *
 * `props` is required, not optional. Every Puck node carries at least an
 * `id`, while a bare `{ type: 'Text' }` is a data object whose discriminator
 * collides with a barrel export name — treating it as a node turns its
 * surrounding data array into a slot and wipes the author's values.
 */
const isComponentNode = (value: unknown): value is ComponentNode => {
  if (!isRecord(value) || typeof value.type !== 'string') return false;
  if (!isRecord(value.props)) return false;
  return (
    value.type === 'PuckText' ||
    RUNTIME_PRIMITIVE_TYPES.has(value.type) ||
    (/^[A-Z]/.test(value.type) && isReactComponent(barrelExports[value.type]))
  );
};

const RESULT_CARD_TYPES = new Set([
  'DegreeCardDetailed',
  'DegreeCardSimple',
  'DegreeCardTiny',
  'RankingCard',
  'SponsoredCard',
]);

function containsResultCard(value: unknown): boolean {
  if (Array.isArray(value)) {
    return value.some(containsResultCard);
  }
  if (!isComponentNode(value)) {
    return false;
  }
  if (RESULT_CARD_TYPES.has(value.type)) {
    return true;
  }
  return Object.values(value.props ?? {}).some(containsResultCard);
}

function isPuckTextNode(value: unknown): value is ComponentNode {
  return isComponentNode(value) && value.type === 'PuckText';
}

function extractPuckText(node: ComponentNode): string {
  const text = node.props?.text;
  return typeof text === 'string' ? text : '';
}

function plainCopyFromNode(node: ComponentNode): string {
  const children = node.props?.children;
  if (typeof children === 'string') return children;
  if (isPuckTextNode(children)) return extractPuckText(children);
  if (Array.isArray(children)) {
    return children
      .map((child) => {
        if (typeof child === 'string') return child;
        if (isPuckTextNode(child)) return extractPuckText(child);
        if (isComponentNode(child)) return plainCopyFromNode(child);
        return '';
      })
      .join('');
  }
  if (isComponentNode(children)) return plainCopyFromNode(children);
  return '';
}

function keepPlainInlineChild(child: unknown): unknown[] {
  if (typeof child === 'string' || isPuckTextNode(child)) return [child];
  if (isComponentNode(child)) {
    const text = plainCopyFromNode(child);
    return text ? [text] : [];
  }
  return [];
}

function sanitizeInlineNode(
  node: ComponentNode,
  insideInlineChild: boolean,
): ComponentNode {
  const props: Record<string, unknown> = { ...(node.props ?? {}) };

  if (insideInlineChild && node.type === 'Text') {
    const tag = typeof props.tag === 'string' ? props.tag.toLowerCase() : 'p';
    if (!INLINE_PHRASING_TAGS.has(tag)) {
      props.tag = 'span';
    }
  }

  if (
    node.type === 'Link' &&
    typeof props.href === 'string' &&
    !isSafeInlineLinkHref(props.href)
  ) {
    delete props.href;
  }

  if (insideInlineChild && 'children' in props) {
    const value = props.children;
    if (Array.isArray(value)) {
      props.children = value.flatMap(keepPlainInlineChild);
    } else if (isComponentNode(value) && !isPuckTextNode(value)) {
      const text = plainCopyFromNode(value);
      if (text) props.children = text;
      else delete props.children;
    }
  }

  return { ...node, props };
}

function sanitizeInlineChildren(
  children: unknown[],
  insideInlineChild: boolean,
): unknown[] {
  const out: unknown[] = [];
  for (const child of children) {
    if (typeof child === 'string') {
      out.push(child);
      continue;
    }
    if (isPuckTextNode(child)) {
      out.push(child);
      continue;
    }
    if (!isComponentNode(child)) continue;
    if (insideInlineChild) {
      out.push(...keepPlainInlineChild(child));
      continue;
    }
    if (!INLINE_CHILD_TYPES.has(child.type)) continue;
    out.push(sanitizeInlineNode(child, true));
  }
  return out;
}

function sanitizeInlineValue(
  value: unknown,
  insideInlineChild: boolean,
): unknown {
  if (Array.isArray(value)) {
    return sanitizeInlineChildren(value, insideInlineChild);
  }
  if (isPuckTextNode(value)) return value;
  if (isComponentNode(value)) {
    if (insideInlineChild) return value;
    if (!INLINE_CHILD_TYPES.has(value.type)) return null;
    return sanitizeInlineNode(value, true);
  }
  return value;
}

function sanitizeInlineHostNode(node: ComponentNode): ComponentNode {
  const props: Record<string, unknown> = { ...(node.props ?? {}) };
  const isHost = INLINE_COPY_HOSTS.has(node.type);

  for (const [key, propVal] of Object.entries(props)) {
    if (key === 'id' || isBindingLanguageKey(key)) continue;
    if (isHost && key === 'children') {
      props[key] = sanitizeInlineValue(propVal, false);
    } else if (Array.isArray(propVal)) {
      props[key] = propVal.map((item) =>
        isComponentNode(item) ? sanitizeInlineHostNode(item) : item,
      );
    } else if (isComponentNode(propVal)) {
      props[key] = sanitizeInlineHostNode(propVal);
    }
  }

  return { ...node, props };
}

function mapZones(
  zones: Data['zones'],
  fn: (value: unknown) => unknown,
): Data['zones'] {
  if (!isRecord(zones)) return zones;
  const out: Record<string, unknown> = {};
  for (const [zoneKey, zoneContent] of Object.entries(zones)) {
    out[zoneKey] = Array.isArray(zoneContent)
      ? zoneContent.map(fn)
      : zoneContent;
  }
  return out as Data['zones'];
}

/**
 * Fail-closed render path for composable copy: one-level Link / phrasing
 * Text only. Recursive Text, block tags, disallowed children, and unsafe
 * hrefs are dropped or coerced before Puck walks the tree.
 *
 * This is a backstop, not the authoring contract. The editor refuses these
 * shapes at edit time; Dynamic Flows publish validation does not check them,
 * so a document written outside the editor is flattened here instead.
 */
function sanitizeInlineCompositionData(data: Data): Data {
  const visit = (value: unknown): unknown => {
    if (Array.isArray(value)) return value.map(visit);
    if (!isComponentNode(value)) return value;
    return sanitizeInlineHostNode(value);
  };

  return {
    ...data,
    content: Array.isArray(data.content)
      ? (data.content.map(visit) as Data['content'])
      : data.content,
    ...(data.zones ? { zones: mapZones(data.zones, visit) } : {}),
  } as Data;
}

/**
 * The admin persists literal copy as synthetic `PuckText` nodes. Those are
 * data — not layout slots — so coerce them back to strings before hen-ui
 * components consume the prop bag.
 */
function coercePuckTextPropValue(value: unknown): unknown {
  if (isPuckTextNode(value)) {
    return extractPuckText(value);
  }
  if (Array.isArray(value) && value.some(isPuckTextNode)) {
    if (value.some((item) => isComponentNode(item) && !isPuckTextNode(item))) {
      return value;
    }
    const out: string[] = [];
    for (const item of value) {
      if (typeof item === 'string') {
        if (item.trim().length > 0) out.push(item);
        continue;
      }
      if (isPuckTextNode(item)) {
        const text = extractPuckText(item);
        if (text.trim().length > 0) out.push(text);
      }
    }
    return out;
  }
  return value;
}

/**
 * Convert leftover `{ type, props }` copy nodes into hen-ui elements so
 * collapsed mixed children never reach React as raw objects. Only hen-ui
 * barrel components (and PuckText) are materialized; other objects keep
 * their original shape so structured data is not wiped.
 */
function materializeInlineTree(value: unknown): unknown {
  if (
    value == null ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    typeof value === 'function' ||
    React.isValidElement(value)
  ) {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((child) => materializeInlineTree(child));
  }
  if (isPuckTextNode(value)) {
    return extractPuckText(value);
  }
  if (isComponentNode(value)) {
    const exported = barrelExports[value.type];
    if (!isReactComponent(exported)) return value;
    const props: Record<string, unknown> = { ...(value.props ?? {}) };
    if ('children' in props) {
      props.children = materializeInlineTree(props.children);
    }
    return React.createElement(
      exported as React.ComponentType<Record<string, unknown>>,
      props,
    );
  }
  return value;
}

/**
 * Detect whether a prop value is a nested component tree (a slot), not a
 * data array.
 *
 * Empty arrays are not slots. Catalog data props such as `dataPointsList`
 * persist `[]` when authors clear the list; treating that as a Puck slot
 * replaces the array with a DropZone function and crashes consumers that
 * call `.slice` / `.map`. Empty layout slots are still covered when any
 * sibling of the same type has real child nodes — slot inference is
 * per-type.
 *
 * Collapsed inline composition (strings mixed with real component nodes
 * such as `Link`) is a slot. All-string and `PuckText`-only/string arrays
 * are excluded: the admin uses them for scalar lists (bullets, tags, etc.),
 * not nested layout trees.
 */
const looksLikeSlotValue = (value: unknown): boolean => {
  if (!Array.isArray(value) || value.length === 0) return false;
  return (
    value.every((item) => typeof item === 'string' || isComponentNode(item)) &&
    value.some((item) => isComponentNode(item) && !isPuckTextNode(item))
  );
};

export type SlotFieldMap = Record<string, Record<string, { type: 'slot' }>>;

/** Walk a `Data` tree to discover which props of each component type are slots. */
export function inferSlotFields(data: Data): SlotFieldMap {
  const slotsByType: SlotFieldMap = {};

  const visit = (node: ComponentNode) => {
    const type = node.type;
    const props = node.props ?? {};

    if (!slotsByType[type]) slotsByType[type] = {};

    for (const [propKey, value] of Object.entries(props)) {
      if (propKey === 'id') continue;
      if (isBindingLanguageKey(propKey)) continue;

      if (Array.isArray(value) && looksLikeSlotValue(value)) {
        slotsByType[type][propKey] = { type: 'slot' };
        value.filter(isComponentNode).forEach(visit);
      } else if (isComponentNode(value) && !isPuckTextNode(value)) {
        // Single embedded node — treat as a single-item slot.
        slotsByType[type][propKey] = { type: 'slot' };
        visit(value);
      }
    }
  };

  data.content.forEach(visit as (item: unknown) => void);
  return slotsByType;
}

/**
 * Wrap a literal string authored inside a slot into the synthetic `PuckText`
 * node the runtime already knows how to render.
 *
 * Puck's slot model is *per-type*, not per-node: once any node of a given type
 * uses a prop as a container (an array of child nodes), that prop is declared a
 * slot for *every* node of that type. A common authoring shape that trips this
 * is a `Text` paragraph (`children` = `[Link, Text, …]`) whose inline runs are
 * themselves `Text` nodes carrying a plain string `children`. Without this
 * wrap those string leaves fall into the `else` branch below and get wiped to
 * `[]` — the text silently disappears. Wrapping them as `PuckText` keeps the
 * slot value a valid child array (so Puck builds its slot render fn) and the
 * literal renders through the same path the editor's literal-text nodes use.
 */
function toSlotChild(
  child: unknown,
  slotFields: SlotFieldMap,
): ComponentNode | null {
  if (typeof child === 'string') {
    return child.trim().length > 0
      ? { type: 'PuckText', props: { text: child } }
      : null;
  }
  if (isComponentNode(child)) return normalizeNode(child, slotFields);
  return null;
}

/**
 * Ensure every prop that the Puck config declares as a slot actually holds an
 * array. Malformed saves (`{}`, `null`, single-node objects) are coerced so
 * Puck's walker doesn't crash on `arr.some`. Literal strings are wrapped as
 * `PuckText` (see `toSlotChild`) so inline text leaves survive.
 */
export function normalizeNode(
  node: ComponentNode,
  slotFields: SlotFieldMap,
): ComponentNode {
  const typeSlots = slotFields[node.type] ?? {};
  const props: Record<string, unknown> = { ...(node.props ?? {}) };

  for (const slotKey of Object.keys(typeSlots)) {
    // Defensive: `inferSlotFields` already excludes binding-language keys,
    // so this branch should be unreachable. If a future refactor moves
    // slot detection elsewhere, this guard prevents a regression where a
    // declaration like `_action.onClick: { type: '…' }` gets coerced into
    // an array of "child nodes" that subsequently break the binding
    // enhancer.
    if (isBindingLanguageKey(slotKey)) continue;
    const current = props[slotKey];
    if (Array.isArray(current)) {
      props[slotKey] = current
        .map((child) => toSlotChild(child, slotFields))
        .filter((child): child is ComponentNode => child !== null);
    } else if (isComponentNode(current)) {
      props[slotKey] = [normalizeNode(current, slotFields)];
    } else if (typeof current === 'string') {
      const wrapped = toSlotChild(current, slotFields);
      props[slotKey] = wrapped ? [wrapped] : [];
    } else {
      props[slotKey] = [];
    }
  }

  return { ...node, props };
}

function normalizeData(data: Data, slotFields: SlotFieldMap): Data {
  return {
    ...data,
    content: (data.content ?? []).map((item) =>
      normalizeNode(item as ComponentNode, slotFields),
    ),
    root: data.root ?? { props: {} },
  } as Data;
}

/**
 * Build the Puck `Config` — a `components` map keyed by `type`, each entry
 * exposing `render` (the hen-ui component) and `fields` (inferred slot
 * declarations so Puck walks children correctly). A root entry is included
 * with an empty fields map to satisfy Puck's walker.
 *
 * Slot materialization
 * --------------------
 * When a field is declared as `{ type: 'slot' }`, Puck's `<Render>` pipeline
 * replaces the prop value with a **DropZone render component** (a function)
 * rather than pre-rendering the child tree. The consumer is expected to mount
 * that function so Puck can emit the nested `DropZoneRenderItem`s. Spreading
 * it straight onto a hen-ui primitive produces
 * `Error: Functions are not valid as a React child` when the component tries
 * to render a raw function in its children slot. We materialize every slot
 * prop by mounting the DropZone with `as={HenUi.SlotPassthrough}` so Puck
 * skips its default `<div>` wrapper and our primitives (Flex, Select, etc.)
 * receive the nested items as direct children — preserving CSS semantics
 * (flexbox, accessible listbox descendants) and keeping the DOM free of
 * layout-invisible wrappers. `SlotPassthrough` is the same helper the
 * hen-ui runtime adapter uses, so admin preview and frontend render agree
 * on slot shape.
 *
 * Why we don't switch to Puck's default `<div>` wrapper for non-layout
 * hosts (e.g. to "fix" Radix `asChild` patterns in Tooltip/Dialog from
 * the renderer side): Puck's `SlotRender` only forwards
 * `{ className, style, ref, children }` to its `as` target — it drops
 * every other prop, including the hover/focus/pointer handlers Radix
 * Slot tries to merge via `asChild`. So *any* wrapper produced by
 * `SlotRender` (Fragment OR `<div>`) loses the merged handlers before
 * they reach the DOM. Composites that rely on Radix `asChild` (Tooltip,
 * Dialog, Popover, …) own that contract themselves by inserting a real
 * DOM element on the Radix side of the slot boundary — see the wrapper
 * span inside `Tooltip.tsx`. That keeps the renderer uniform and
 * confines the workaround to the components that actually need it.
 */
/** The render-prop shape Puck's slot value exposes (a DropZone function). */
export type SlotRenderFn = (props: {
  as?: React.ComponentType<{ children?: React.ReactNode }>;
}) => React.ReactNode;

/**
 * Stable-identity boundary for a Puck slot value.
 *
 * Puck rebuilds a slot's render function (`getSlotTransform` /
 * `useFieldTransforms` in `@puckeditor/core`) with a NEW function identity
 * on every render. Rendering that function directly as the element type
 * (`<Slot as={…}/>`) makes React see a new component type each render and
 * REMOUNT the entire slotted subtree — which resets focus/uncontrolled
 * state in descendants on every re-render. Because the DSL runtime
 * re-renders the whole `<Render>` tree whenever `DataSourceManager`
 * form-state changes (e.g. every keystroke in a bound text `Input`), that
 * remount made free-text inputs accept only a single character before
 * losing focus.
 *
 * `StableSlot` has a stable type across renders, so React reconciles the
 * subtree in place. The changing Puck function is passed as a prop and
 * invoked to yield Puck's own stable `SlotRenderPure` element (whose
 * children are keyed by node id), so nothing below remounts.
 *
 * Hosts that `Children.toArray` slotted items (catalog
 * `resolveSlotChildren`) skip this path and use `HenUi.SlotHost` instead,
 * which inverts the slot through Puck's `as` so the host receives real
 * child elements. Accordion and other mount-the-slot hosts stay here.
 */
export const StableSlot: React.FC<{
  render: SlotRenderFn;
  inline?: boolean;
}> = ({ render, inline = false }) => (
  <>
    {render({
      as: inline ? HenUi.InlineSlotElement : HenUi.SlotPassthrough,
    })}
  </>
);

const materializeSlotValue = (
  value: unknown,
  inline = false,
): React.ReactNode => {
  if (typeof value === 'function') {
    return <StableSlot render={value as SlotRenderFn} inline={inline} />;
  }
  return value as React.ReactNode;
};

/**
 * Mutable handle returned alongside the config so callers can swap the
 * inner-template Puck `Config` used by `platform.dataRepeater` after
 * `applyBindings` produces an enhanced (binding-wrapped) clone. The
 * dataRepeater render closes over `boundConfigRef` so the per-row
 * `<Render>` resolves child types against the **enhanced** components,
 * not the raw ones — without this, `_bind.*` declarations on every
 * EditorialListing (or any other catalog component) inside `itemSlot`
 * would be dropped because the inner `<Render>` would walk the
 * pre-`applyBindings` map. Default value is the un-enhanced config so
 * that pure-Puck DSLs (no binding pass) still render correctly.
 */
export type BoundConfigRef = { current: Config };

interface BuildPuckConfigResult {
  readonly config: Config;
  readonly boundConfigRef: BoundConfigRef;
}

/**
 * Resolves the components a slot host (`Tabs`, `StatColumns`) rebuilds from
 * its raw slot content. hen-ui names come from the barrel. The runtime
 * primitives are not on the barrel, so each gets a stable wrapper that
 * renders the entry from the bound config. The wrapper owns its slot props:
 * `fallback`, `itemSlot` and `outcomeSlotN` must stay raw JSON for the
 * renderer's own `<Render>`.
 */
function createSlotComponentResolver(
  getConfig: () => Config,
): (exportName: string) => SlotHostComponent | null {
  const primitives = new Map<string, SlotHostComponent>();

  const primitiveFor = (type: string): SlotHostComponent => {
    const cached = primitives.get(type);
    if (cached) return cached;
    const RuntimePrimitive: SlotHostComponent = Object.assign(
      (props: Record<string, unknown>) => {
        const render = getConfig().components[type]?.render;
        return render
          ? React.createElement(
              render as React.ComponentType<Record<string, unknown>>,
              props,
            )
          : null;
      },
      { ownsSlotProps: true as const, displayName: type },
    );
    primitives.set(type, RuntimePrimitive);
    return RuntimePrimitive;
  };

  return (exportName) => {
    const exported = barrelExports[exportName];
    if (isReactComponent(exported)) {
      return exported as React.ComponentType<Record<string, unknown>>;
    }
    return RUNTIME_PRIMITIVE_TYPES.has(exportName)
      ? primitiveFor(exportName)
      : null;
  };
}

function buildPuckConfig(slotFields: SlotFieldMap): BuildPuckConfigResult {
  const components: Config['components'] = {};
  const boundConfigRef: BoundConfigRef = { current: undefined as never };
  const resolveSlotComponent = createSlotComponentResolver(
    () => boundConfigRef.current ?? config,
  );

  for (const [type, exported] of Object.entries(barrelExports)) {
    if (!isReactComponent(exported)) continue;

    const Component =
      type === 'DegreeCardDetailed'
        ? DslDegreeCardDetailed
        : type === 'EditorialListing'
          ? DslEditorialListing
          : type === 'CareerExplorer'
            ? DslCareerExplorer
            : (exported as React.ComponentType<Record<string, unknown>>);
    const slotKeys = Object.keys(slotFields[type] ?? {});

    components[type] = {
      fields: (slotFields[type] ??
        {}) as Config['components'][string]['fields'],
      render: (props: Record<string, unknown>) => {
        const {
          editMode: _editMode,
          puck: _puck,
          editorRootRef: _editorRootRef,
          ...rest
        } = props ?? {};
        for (const [key, value] of Object.entries(rest)) {
          if (key === 'id' || isBindingLanguageKey(key)) continue;
          if (slotKeys.includes(key)) continue;
          rest[key] = materializeInlineTree(coercePuckTextPropValue(value));
        }
        for (const key of slotKeys) {
          if (key in rest) {
            if (
              key === 'children' &&
              typeof HenUi.shouldResolveSlotChildren === 'function' &&
              HenUi.shouldResolveSlotChildren(type)
            ) {
              continue;
            }
            rest[key] = materializeSlotValue(
              rest[key],
              INLINE_COPY_HOSTS.has(type) && key === 'children',
            );
          }
        }
        // `transform: 'options'` resolves to a flat `{ label, value }[]`, which
        // `Select` and `ToggleGroup` cannot read — both are compound controls
        // that take item children. Shared with every other host registering
        // hen-ui components, so a correctly authored `_bind.options` populates
        // the control wherever it is rendered rather than only here.
        const renderedSource =
          typeof HenUi.omitUnsetOptionalEnumProps === 'function'
            ? HenUi.omitUnsetOptionalEnumProps(
                rest,
                HenUi.OPTIONAL_ENUM_PROP_NAMES?.[type],
              )
            : rest;
        const renderedProps = synthesizeOptionChildren(type, renderedSource);
        if (
          typeof HenUi.shouldResolveSlotChildren === 'function' &&
          HenUi.shouldResolveSlotChildren(type) &&
          typeof renderedProps.children === 'function' &&
          HenUi.SlotHost
        ) {
          const { children: slotFn, ...hostProps } = renderedProps;
          return (
            <HenUi.SlotHost
              Host={Component}
              hostProps={hostProps}
              slot={slotFn as SlotRenderFn}
              resolveComponent={resolveSlotComponent}
              mode='render'
            />
          );
        }
        return <Component {...renderedProps} />;
      },
    };
  }

  // `PuckText` is a synthetic helper that the admin's `createPuckConfig`
  // registers so authors can drop literal text into any slot (Text body,
  // Button label, SelectItem option, etc.). It is *not* exported from the
  // hen-ui barrel, so we mirror its render contract here — otherwise every
  // text leaf round-tripped from the editor would silently disappear.
  components.PuckText = {
    fields: {},
    render: (props: Record<string, unknown>) => {
      const tag =
        typeof props.tag === 'string' && props.tag ? props.tag : 'span';
      const text = typeof props.text === 'string' ? props.text : '';
      return React.createElement(tag, null, text);
    },
  };

  const config: Config = {
    components,
    root: {
      fields: {},
      render: ({ children }: { children?: React.ReactNode }) => <>{children}</>,
    },
  } as Config;

  // `platform.dataRepeater` is a synthetic structural component (SAD §10
  // / ADR-13). The DSL author drops a per-row template into `itemSlot`
  // and DataRepeater iterates the resolved data source, rendering the
  // template once per row inside an `ItemScopeProvider`.
  //
  // We declare `fields: {}` so Puck does NOT wrap `itemSlot` as a
  // DropZone function — we want the raw JSON template here so each
  // row can mount its own `<Render>` of the same template inside its
  // own item scope. `inferSlotFields` still walks the inner template
  // children so nested HEN components keep their own slot detection.
  //
  // The per-row `<Render>` MUST reference the enhanced (binding-wrapped)
  // config produced by `applyBindings`, otherwise every `_bind.*` /
  // `_action.*` / `formKey` declaration on a catalog component inside
  // `itemSlot` is dropped (`createBoundRender` is the layer that walks
  // the prop bag and resolves them). `boundConfigRef.current` is set
  // by the DslConfig caller after `applyBindings` runs; before that
  // and for non-binding DSLs it falls back to the raw `config`, so
  // pure-Puck content still renders correctly.
  components['platform.dataRepeater'] = {
    fields: {},
    render: (props: Record<string, unknown>) => {
      const {
        itemSlot,
        repeatInsideShell,
        editMode: _e,
        puck: _p,
        ...rest
      } = props ?? {};
      const template = Array.isArray(itemSlot)
        ? (itemSlot as ComponentNode[])
        : [];
      const templateData = {
        content: template,
        root: { props: {} },
      } as unknown as Data;
      // The source name must come from the DSL `source` prop. We cast
      // through `unknown` because Puck hands us a fully-untyped prop
      // bag — the downstream binding enhancer + DataRepeater each
      // validate their own contracts at runtime (loud loading state if
      // `source` resolves to nothing).
      const repeaterProps = rest as unknown as React.ComponentProps<
        typeof DataRepeater
      >;
      const loadingVariant = containsResultCard(template) ? 'card' : 'rows';
      const innerConfig = boundConfigRef.current ?? config;
      if (repeatInsideShell === true) {
        const shell = template.length === 1 ? template[0] : undefined;
        const rawChildren = shell?.props?.children;
        const shellEntry = shell
          ? innerConfig.components[shell.type]
          : undefined;
        const hasSingleChildTemplate =
          Array.isArray(rawChildren) &&
          rawChildren.length === 1 &&
          isAuthoredNode(rawChildren[0]);
        if (!shell || !shellEntry || !hasSingleChildTemplate) {
          warnOnce(
            'platform.dataRepeater',
            '[platform.dataRepeater] repeatInsideShell requires one shell holding exactly one child template.',
          );
          return <></>;
        }
        return (
          <DataRepeater
            {...repeaterProps}
            repeatInsideShell
            loadingVariant={loadingVariant}
          >
            {renderAuthoredNode(shell, innerConfig, slotFields)}
          </DataRepeater>
        );
      }
      return (
        <DataRepeater {...repeaterProps} loadingVariant={loadingVariant}>
          <Render config={innerConfig} data={templateData} />
        </DataRepeater>
      );
    },
  };

  // `platform.variant` mounts one of its eight `outcomeSlotN` arrays, chosen
  // by the outcome the page host embedded in `context.decisions`. Like the
  // repeater it declares `fields: {}`, so its slots arrive as raw arrays and
  // each is rendered through the binding-wrapped config only once selected.
  components[VARIANT_TYPE] = {
    fields: {},
    render: (props: Record<string, unknown>) => {
      const {
        id,
        decisionId,
        outcomeSlots,
        fallbackSlot,
        reservedSize,
        editMode: _e,
        puck: _p,
      } = props ?? {};
      const innerConfig = boundConfigRef.current ?? config;
      const renderSlot = (slot: string) => {
        const children = VARIANT_SLOT_NAMES.includes(slot)
          ? props[slot]
          : undefined;
        const content = Array.isArray(children)
          ? (children as ComponentNode[])
          : [];
        if (content.length === 0) return null;
        return (
          <Render
            config={innerConfig}
            data={{ content, root: { props: {} } } as unknown as Data}
          />
        );
      };
      return (
        <Variant
          id={typeof id === 'string' ? id : undefined}
          decisionId={typeof decisionId === 'string' ? decisionId : ''}
          outcomeSlots={
            outcomeSlots && typeof outcomeSlots === 'object'
              ? (outcomeSlots as Record<string, unknown>)
              : undefined
          }
          fallbackSlot={
            typeof fallbackSlot === 'string' ? fallbackSlot : undefined
          }
          reservedSize={
            reservedSize && typeof reservedSize === 'object'
              ? (reservedSize as HostSlotReservedSize)
              : undefined
          }
          renderSlot={renderSlot}
        />
      );
    },
  };

  // `platform.hostSlot` takes the same `fields: {}` + `boundConfigRef` route
  // as the repeater, so `fallback` arrives as raw JSON and renders through
  // the binding-wrapped components.
  components['platform.hostSlot'] = {
    fields: {},
    render: (props: Record<string, unknown>) => {
      const { fallback, editMode: _e, puck: _p, ...rest } = props ?? {};
      const fallbackNodes = Array.isArray(fallback)
        ? (fallback as ComponentNode[])
        : [];
      const hostSlotProps = rest as unknown as React.ComponentProps<
        typeof HostSlot
      >;
      const innerConfig = boundConfigRef.current ?? config;
      return (
        <HostSlot {...hostSlotProps} fallbackEmpty={fallbackNodes.length === 0}>
          {fallbackNodes.length > 0 ? (
            <Render
              config={innerConfig}
              data={
                {
                  content: fallbackNodes,
                  root: { props: {} },
                } as unknown as Data
              }
            />
          ) : null}
        </HostSlot>
      );
    },
  };

  // `platform.form` renders its `children` through the binding-wrapped config,
  // like `platform.hostSlot`, so its fields read and write `formState`.
  components[FORM_COMPONENT_TYPE] = {
    fields: {},
    render: (props: Record<string, unknown>) => {
      const { name, children } = props ?? {};
      const childNodes = Array.isArray(children)
        ? (children as ComponentNode[])
        : [];
      return (
        <PlatformForm name={name}>
          {childNodes.length > 0 ? (
            <Render
              config={boundConfigRef.current ?? config}
              data={
                { content: childNodes, root: { props: {} } } as unknown as Data
              }
            />
          ) : null}
        </PlatformForm>
      );
    },
  };

  boundConfigRef.current = config;
  return { config, boundConfigRef };
}

interface DslErrorBoundaryProps {
  children: React.ReactNode;
  configId?: string;
  fallback?: DslRenderErrorFallback;
  onRenderError?: DslRenderErrorHandler;
  resetKey?: unknown;
}

interface DslErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  info?: React.ErrorInfo;
}

/**
 * SAD §14.3: a malformed DSL payload must not crash the surrounding flow step.
 * We catch, log, and render nothing. Legacy fallback is the caller's job.
 */
class DslConfigErrorBoundary extends React.Component<
  DslErrorBoundaryProps,
  DslErrorBoundaryState
> {
  state: DslErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(error: Error): DslErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error(
      `[DslConfig] Render error for config "${this.props.configId ?? 'unknown'}":`,
      error,
      info.componentStack,
    );
    this.props.onRenderError?.(error, info);
    if (typeof this.props.fallback === 'function') this.setState({ info });
  }

  componentDidUpdate(
    prevProps: DslErrorBoundaryProps,
    prevState: DslErrorBoundaryState,
  ): void {
    if (prevState.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: undefined, info: undefined });
    }
  }

  render(): React.ReactNode {
    if (!this.state.hasError) return this.props.children;

    const { fallback } = this.props;

    if (typeof fallback === 'function' && this.state.error) {
      return fallback(this.state.error, this.state.info);
    }

    return (fallback as React.ReactNode) ?? null;
  }
}

export interface DslConfigProps {
  dslConfig?: DslConfigPayload;
  excludedContentTypes?: readonly string[];
  renderErrorFallback?: DslRenderErrorFallback;
  onRenderError?: DslRenderErrorHandler;
}

/**
 * Read `data.root.props.dataSources` (DSL convention from child SAD §5.2)
 * and validate its shape just enough to feed the DSM provider. Returns
 * `null` when no DSL bindings are declared, in which case the original
 * (un-enhanced) Puck render path is used.
 */
/**
 * Cheap scan for DSL binding markers on any node in the tree. Returns
 * true if at least one prop key starts with `_bind.`, `_action.`, or
 * is `formKey`. Used to gate `applyBindings`: flows without DSM data
 * sources can still ship pure `_action.*` declarations (e.g. wiring
 * a Button onClick to flow navigation) and need the enhancer pass.
 */
function readsInjectedRoot(
  value: unknown,
  seen = new WeakSet<object>(),
): boolean {
  if (typeof value === 'string') {
    if (!value.includes('{{')) return false;
    try {
      return extractPaths(value).some((path) => {
        const [head, next] = path.split('.');
        // The host fills `context.decisions`, so copy that reads it needs
        // the enhancer even when nothing else on the page is bound.
        return (
          head === 'route' ||
          head === 'scope' ||
          (head === 'context' && next === DECISIONS_CONTEXT_KEY)
        );
      });
    } catch {
      return false;
    }
  }
  if (value === null || typeof value !== 'object') return false;
  if (seen.has(value)) return false;
  seen.add(value);
  const items = Array.isArray(value)
    ? value
    : Object.values(value as Record<string, unknown>);
  return items.some((item) => readsInjectedRoot(item, seen));
}

function hasTemplatedInput(inputs: unknown): boolean {
  return (
    isRecord(inputs) &&
    Object.values(inputs).some(
      (value) => typeof value === 'string' && value.includes('{{'),
    )
  );
}

function hasBindingMarkers(data: Data | null): boolean {
  if (!data) return false;
  let found = false;
  const visit = (node: unknown): void => {
    if (found || !node) return;
    if (Array.isArray(node)) {
      for (const child of node) visit(child);
      return;
    }
    if (typeof node !== 'object') return;
    const obj = node as Record<string, unknown>;
    const props = (obj.props as Record<string, unknown> | undefined) ?? null;
    // Host slot `inputs` are plain `{{…}}` templates with no marker key, and
    // only the enhancer resolves them.
    if (obj.type === 'platform.hostSlot' && hasTemplatedInput(props?.inputs)) {
      found = true;
      return;
    }
    if (props) {
      for (const key of Object.keys(props)) {
        if (
          key === 'formKey' ||
          key.startsWith('_bind.') ||
          key.startsWith('_action.') ||
          // `_meta.tracking` on any node (notably a
          // non-default-tracked Button) must
          // activate the binding enhancer so `Trackable` mounts and
          // the click dispatcher attaches. Pre-fix this gate only
          // looked at `_bind.*` / `_action.*` / `formKey`, so a
          // tracking-only Button rendered through raw Puck with no
          // wrapper. The narrow `_meta.tracking` match (not the
          // whole `_meta.` prefix) preserves the "pure presentational
          // DSL, no Trackable, no DSM" path for legacy configs that
          // may carry other `_meta.*` props for non-tracking reasons.
          key === '_meta.tracking' ||
          // `_meta.visibility` (EDUN-14346) gates whether a node mounts
          // at all (value predicate `when`, breakpoint `showOn`). A node
          // gated ONLY on visibility — no `_bind.*` / `_action.*` /
          // `formKey` / `_meta.tracking` — must still get the enhancer
          // pass so `createBoundRender`'s `VisibilityGate` can evaluate
          // it and true-unmount a non-match. Same narrow exact-key match
          // as `_meta.tracking` (NOT a blanket `_meta.*`), preserving the
          // pure-presentational path for configs carrying unrelated
          // `_meta.*` props.
          key === '_meta.visibility' ||
          // A scroll point is stamped on the rendered block by
          // `createBoundRender`, so a block named only by its scroll
          // point still needs the enhancer pass for StickyBar to find it.
          key === '_meta.scrollPoint' ||
          // `_scope` publishes a bag for descendants. A document whose
          // only annotation is that key still needs the enhancer so the
          // provider wraps the node.
          key === '_scope'
        ) {
          found = true;
          return;
        }
      }
      if (readsInjectedRoot(props)) {
        found = true;
        return;
      }
      for (const value of Object.values(props)) visit(value);
    }
  };
  visit((data as { content?: unknown[] }).content ?? []);
  const zones = (data as { zones?: unknown }).zones;
  if (isRecord(zones)) visit(Object.values(zones));
  return found;
}

/**
 * Walks the tree looking for any node whose `type` is registered in
 * {@link COMPONENT_TRACKING_DEFAULTS}. Used to widen the
 * `applyBindings` gate so a purely presentational DSL config (no
 * `_bind.*` / `_action.*` / `formKey` / `dataSources`) still gets
 * wrapped in `<Trackable>` when the registry says one of its
 * component types should fire events. The walk covers `content`,
 * `zones`, and nested props, so a registered node that lives only in
 * a Puck zone still opts in. Without this, Option B
 * descriptor-driven tracking would silently no-op on legacy
 * binding-free configs (the same payload shape that ships today for
 * static hero/landing surfaces).
 */
function hasTrackedComponent(
  data: Data | null,
  trackingDefaults: Readonly<Record<string, unknown>>,
): boolean {
  if (!data) return false;
  let found = false;
  const visit = (node: unknown): void => {
    if (found || node == null) return;
    if (Array.isArray(node)) {
      for (const child of node) visit(child);
      return;
    }
    if (typeof node !== 'object') return;
    const obj = node as Record<string, unknown>;
    if (typeof obj.type === 'string') {
      if (Object.prototype.hasOwnProperty.call(trackingDefaults, obj.type)) {
        found = true;
        return;
      }
      const props = obj.props;
      if (props && typeof props === 'object') {
        for (const value of Object.values(props as Record<string, unknown>)) {
          visit(value);
        }
      }
      return;
    }
    // Zones and other containers have no component type. Descend them so a
    // registered node that exists only under `data.zones` still opts in.
    for (const value of Object.values(obj)) visit(value);
  };
  visit(data);
  return found;
}

function readDataSources(
  data: Data | null,
): Record<string, DataSourceDeclaration> | null {
  const root = (data?.root as { props?: Record<string, unknown> } | undefined)
    ?.props;
  const declared = root?.dataSources;
  if (!declared || typeof declared !== 'object') return null;
  const entries = Object.entries(declared as Record<string, unknown>);
  if (entries.length === 0) return null;
  const valid: Record<string, DataSourceDeclaration> = {};
  for (const [name, decl] of entries) {
    if (!decl || typeof decl !== 'object') continue;
    const d = decl as DataSourceDeclaration;
    if (isSupportedDataSourceDeclaration(d)) {
      valid[name] = d;
    }
  }
  return Object.keys(valid).length > 0 ? valid : null;
}

function excludeContentTypes(
  data: unknown,
  excludedTypes?: readonly string[],
): unknown {
  if (!excludedTypes || excludedTypes.length === 0) return data;
  if (!data || typeof data !== 'object') return data;
  const content = (data as { content?: unknown }).content;
  if (!Array.isArray(content)) return data;

  const excluded = new Set(excludedTypes);
  return {
    ...(data as Record<string, unknown>),
    content: content.filter((node) => {
      if (!node || typeof node !== 'object') return true;
      const type = (node as { type?: unknown }).type;
      return typeof type !== 'string' || !excluded.has(type);
    }),
  };
}

/**
 * Build an {@link ActionDispatcher} that bridges DSL `_action.{event}`
 * declarations to the surrounding flow's navigation API.
 *
 * Authoring contract: a DSL component declares
 *   `_action.onClick: { type: 'flow.forward' }`
 * and the binding enhancer emits an `onClick` handler that calls
 * `dispatcher.dispatch('flow.forward')` → which lands here and fires
 * `handleStepForward()` on the active flow. `flow.back` mirrors the
 * same shape against `handlePrevious`. Unknown types fall through to
 * a console warning (no throw) so an authoring typo can't crash the
 * step. `params` are accepted but ignored for navigation today —
 * future action types will read them.
 */
function useFlowActionDispatcher(): ActionDispatcher {
  const ctx = useDynamicExperienceContext();
  const handleStepForward = ctx?.handleStepForward;
  const handlePrevious = ctx?.handlePrevious;
  const modalHost =
    typeof useModalHostOptional === 'function' ? useModalHostOptional() : null;

  return useMemo<ActionDispatcher>(() => {
    const handlers: Record<
      string,
      (params?: Readonly<Record<string, unknown>>) => void
    > = {};
    if (handleStepForward) {
      handlers['flow.forward'] = () => {
        void handleStepForward();
      };
    }
    if (handlePrevious) {
      handlers['flow.back'] = () => {
        handlePrevious();
      };
    }
    if (modalHost) {
      handlers['modal.open'] = (params) => {
        dispatchNamedModalAction(modalHost, 'modal.open', params, 'DslConfig');
      };
      handlers['modal.close'] = (params) => {
        dispatchNamedModalAction(modalHost, 'modal.close', params, 'DslConfig');
      };
    }
    return {
      dispatch(type: string, params?: Readonly<Record<string, unknown>>): void {
        const fn = handlers[type];
        if (!fn) {
          // eslint-disable-next-line no-console
          console.warn(
            `[DslConfig] No handler registered for action "${type}" — dropping.`,
          );
          return;
        }
        fn(params);
      },
      has(type: string): boolean {
        return type in handlers;
      },
    };
  }, [handleStepForward, handlePrevious, modalHost]);
}

const DslConfig: React.FC<DslConfigProps> = ({
  dslConfig,
  excludedContentTypes,
  renderErrorFallback,
  onRenderError,
}) => {
  const configId = dslConfig?.configId;
  const standaloneActionDispatcher = useFlowActionDispatcher();

  /**
   * Detect whether a host bridge (`ConfigurableStep` per child SAD
   * ADR-15) already mounted the DSM and ActionDispatcher above us. If
   * so, the bridge owns the providers — wiring our own would shadow
   * `flowContext`, `onFormStateChange`, and the `navigation.*`
   * dispatcher (PR #2372 review §1).
   *
   * Detection is by reference, not by prop: the bridge cannot hand a
   * flag through Puck's `<Render>` boundary, and forcing every render
   * site to thread an explicit `withStandaloneProviders={false}` couples
   * the bridge to internal `DslConfig` plumbing. `useContext` reads
   * are O(1) and only run once per render so the cost is negligible.
   *
   * - DSM: `useOptionalDataSourceManager()` returns `undefined` when no
   *   provider is mounted; any defined value indicates host ownership.
   * - ActionDispatcher: the context default is the singleton
   *   `NOOP_ACTION_DISPATCHER`. A reference-inequal value means a real
   *   provider supplied a dispatcher upstream.
   */
  const parentDsm = useOptionalDataSourceManager();
  const parentDispatcher = useContext(ActionDispatcherContext);
  const parentComparison = useComparisonOptional();
  const parentResultsPageConfig = useResultsPageConfigOptional();
  const hostOwnsDsm = parentDsm !== undefined;
  const hostOwnsDispatcher = parentDispatcher !== NOOP_ACTION_DISPATCHER;
  const hostOwnsComparison = parentComparison !== undefined;
  const hostOwnsResultsPageConfig = parentResultsPageConfig !== undefined;
  const tracking = useTracking();
  const trackingDefaults = useMemo(
    () =>
      overlayComponentTrackingDefaults(
        COMPONENT_TRACKING_DEFAULTS,
        tracking?.host?.componentTrackingDefaults,
      ),
    [tracking?.host?.componentTrackingDefaults],
  );

  const { config, data, dataSources, bindingsApplied, resultsPageConfig } =
    useMemo(() => {
      const raw = excludeContentTypes(dslConfig?.data, excludedContentTypes) as
        | Data
        | undefined;
      if (!raw || !Array.isArray((raw as { content?: unknown }).content)) {
        return {
          config: null as Config | null,
          data: null as Data | null,
          dataSources: null as Record<string, DataSourceDeclaration> | null,
          bindingsApplied: false,
          resultsPageConfig: undefined,
        };
      }

      const sanitized = sanitizeInlineCompositionData(raw);
      const slotFields = inferSlotFields(sanitized);
      const { config: puckConfig, boundConfigRef } =
        buildPuckConfig(slotFields);
      const normalized = normalizeData(sanitized, slotFields);
      const sources = readDataSources(normalized);
      // Apply the binding enhancer whenever the payload either declares
      // DSM data sources, carries any `_bind.*` / `_action.*` / `formKey`
      // marker, OR includes a node whose hen-ui type registers a default
      // tracking descriptor. Pure Puck content
      // with none of the above still skips the pass and stays
      // bit-identical with the legacy path.
      const needsBindings =
        Boolean(sources) ||
        hasBindingMarkers(normalized) ||
        hasTrackedComponent(normalized, trackingDefaults);
      // Per-type tracking defaults are wired here so every DSL node lights
      // up `<Trackable>` without forcing authors to spell out
      // `_meta.tracking.kind` on every saved config. Author-supplied
      // `_meta.tracking.*` keys still win (merge order owned by
      // `createBoundRender`). Host defaults overlay the shared registry.
      const boundConfig = needsBindings
        ? applyBindings(puckConfig, undefined, trackingDefaults)
        : puckConfig;
      // The variant is gated by its decision, not by `_meta.visibility`, so it
      // keeps its unwrapped render. Its slot children still render bound.
      const finalConfig =
        boundConfig === puckConfig
          ? puckConfig
          : {
              ...boundConfig,
              components: {
                ...boundConfig.components,
                [VARIANT_TYPE]: puckConfig.components[VARIANT_TYPE],
              },
            };
      // `platform.dataRepeater` closes over `boundConfigRef` so the
      // per-row `<Render>` walks the binding-wrapped components, not the
      // raw ones. Without this swap every `_bind.*` inside `itemSlot`
      // would be dropped (the inner `<Render>` would resolve children
      // against the pre-`applyBindings` map).
      boundConfigRef.current = finalConfig;
      const factBindings = collectFactBindings(normalized);
      return {
        config: finalConfig,
        data: normalized,
        // A source only conditional variant bindings read is fetched only
        // while one of their preconditions holds.
        dataSources: applyFetchPreconditions(sources, factBindings, normalized),
        bindingsApplied: needsBindings,
        resultsPageConfig: extractResultsPageConfig(normalized),
      };
    }, [dslConfig, excludedContentTypes, trackingDefaults]);

  // Inner DSL `dataSources` are silently ignored when a host bridge
  // already owns the provider — `ConfigurableStep` builds the same
  // declarations from the same DSL via `extractDataSources`, so
  // double-mounting would only fragment caches and split `flowContext`.
  // Surface the conflict once per render so authors discover stale or
  // conflicting declarations during development.
  if (hostOwnsDsm && dataSources) {
    // eslint-disable-next-line no-console
    console.warn(
      `[DslConfig] Ignoring inner dataSources for "${configId ?? 'unknown'}" — host bridge owns the DataSourceManager.`,
    );
  }

  if (!config || !data) {
    console.warn(
      `[DslConfig] Missing or malformed payload for config "${configId ?? 'unknown'}" — rendering nothing.`,
    );
    return null;
  }

  const renderTreeContent = (
    <DslConfigErrorBoundary
      configId={configId}
      fallback={renderErrorFallback}
      onRenderError={onRenderError}
      // Passing any fallback, even null, opts into recovery; the live path omits it and keeps its error state.
      resetKey={renderErrorFallback !== undefined ? config : undefined}
    >
      <DslConfigOwnerProvider tenantId={dslConfig?.tenantId}>
        <Render config={config} data={data} />
      </DslConfigOwnerProvider>
    </DslConfigErrorBoundary>
  );

  const renderTree = hostOwnsResultsPageConfig ? (
    renderTreeContent
  ) : (
    <ResultsPageConfigProvider config={resultsPageConfig}>
      {renderTreeContent}
    </ResultsPageConfigProvider>
  );

  const comparisonConfig = resultsPageConfig?.comparison;
  const comparisonEnabled = comparisonConfig?.enabled === true;
  const withComparison =
    hostOwnsComparison || !comparisonEnabled ? (
      renderTree
    ) : (
      <ComparisonProvider
        minPrograms={comparisonConfig?.minSelection}
        maxPrograms={comparisonConfig?.maxSelection}
      >
        {renderTree}
        <ComparisonRuntime />
      </ComparisonProvider>
    );

  // Wrap with our own ActionDispatcher only when no host bridge is
  // present. Authors who write `_action.onClick: { type: 'flow.forward' }`
  // on a standalone DSL render still get the legacy navigation
  // dispatcher; under `ConfigurableStep` they bind to the bridge's
  // `navigation.*` dispatcher instead.
  const withActions = hostOwnsDispatcher ? (
    withComparison
  ) : (
    <ActionDispatcherContext.Provider value={standaloneActionDispatcher}>
      {withComparison}
    </ActionDispatcherContext.Provider>
  );

  // Mount the standalone DSM whenever the host did NOT already supply
  // one AND the binding enhancer was applied. The host case keeps
  // `flowContext` and `onFormStateChange` wiring intact end-to-end
  // (PR #2372 review §1, AC 1–4).
  //
  // Why we mount even when `dataSources` is null: `applyBindings`
  // wraps every component in a `BoundComponent` that unconditionally
  // calls `useDataSourceManager()`. An action-only DSL payload —
  // e.g. a Button with `_action.onClick: { type: 'flow.forward' }`
  // and a `formKey` capture, but no `root.props.dataSources` — still
  // goes through `BoundComponent`, so it needs a DSM in scope or it
  // throws at first render. Mounting an empty DSM costs the same as
  // mounting one with a populated map: `topologicalSort({})` returns
  // `{ ordered: [], missingDependencies: [] }`, no fetches fire, and the
  // `formState` reducer starts empty.

  if (hostOwnsDsm || !bindingsApplied) {
    return withActions;
  }

  return (
    <DataSourceManagerProvider
      dataSources={dataSources ?? {}}
      flowContext={{ request: projectRequestContext() }}
    >
      {withActions}
    </DataSourceManagerProvider>
  );
};

export default DslConfig;
